import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import StaticListProductListLine from "@insite/content-library/Widgets/StaticList/StaticListProductListLine";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getQuoteState } from "@MasterBluePrint/Store/Data/Quotes/QuotesSelector";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const wishListState = getWishListState(state, state.pages.staticList.wishListId);
    const productInfosByWishListLineId = state.pages.staticList.productInfosByWishListLineId;
    return {
        wishListState,
        productInfosByWishListLineId,
        quoteState: getQuoteState(state, wishListState.value?.description),
    };
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StaticListProductListStyles {
    mainWrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    linesWrapper?: InjectableCss;
}

export const staticListProductListStyles: StaticListProductListStyles = {
    mainWrapper: {
        css: css`
            margin-top: 10px;

            .qty-wrapper {
                > div {
                    max-width: initial;
                    flex-basis: initial;
                }
            }

            .qty-container-new {
                margin-top: 0 !important;

                div[class^="FormFieldStyle"],
                div[class*=" FormFieldStyle"] {
                    div[class^="FormInputWrapper"],
                    div[class*=" FormInputWrapper"] {
                        margin-top: 10px !important;
                    }
                }

                .input-container button {
                    top: 2.1rem !important;
                }

                @media screen and (max-width: 767px) {
                    .input-container > div > div {
                        margin-top: 10px !important;
                    }
                }

                input[data-test-selector="product_qtyOrdered"] {
                    font-family: Roboto, sans-serif;
                    letter-spacing: 0.16px;
                    text-align: center;
                    width: 9rem !important;
                    min-width: 9rem !important;
                    color: rgb(51, 63, 71) !important;
                    font-size: 0.875rem !important;
                    padding: 0px 2.5rem !important;
                    background: rgb(255, 255, 255) !important;
                }
            }
        `,
    },
    countText: {
        weight: 600,
    },
    linesWrapper: {
        css: css`
            margin-top: 10px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
};

const styles = staticListProductListStyles;

const StaticListProductList = ({ wishListState, productInfosByWishListLineId, quoteState }: Props) => {
    if (wishListState.isLoading || !wishListState.value || Object.keys(productInfosByWishListLineId).length === 0) {
        return null;
    }

    const wishList = wishListState.value;
    const sharedQuote = quoteState.value;

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography {...styles.countText}>
                {wishList.wishListLinesCount === 1 && translate("{0} Product", wishList.wishListLinesCount.toString())}
                {wishList.wishListLinesCount > 1 && translate("{0} Products", wishList.wishListLinesCount.toString())}
            </Typography>
            <StyledWrapper {...styles.linesWrapper}>
                {!sharedQuote && (
                    <>
                        {wishList.wishListLineCollection?.map(wishListLine => (
                            <StaticListProductListLine
                                key={wishListLine.id}
                                wishListLine={wishListLine}
                                productInfo={productInfosByWishListLineId[wishListLine.id]!}
                            />
                        ))}
                    </>
                )}
                {sharedQuote && (
                    <>
                        {wishList.wishListLineCollection?.map(wishListLine => (
                            <StaticListProductListLine
                                key={wishListLine.id}
                                wishListLine={wishListLine}
                                productInfo={productInfosByWishListLineId[wishListLine.id]!}
                                sharedQuoteLine={sharedQuote.quoteLineCollection?.find(
                                    item => item.productId === wishListLine.productId,
                                )}
                            />
                        ))}
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(StaticListProductList),
    definition: {
        group: "Static List",
        displayName: "Product List",
        allowedContexts: ["StaticListPage"],
    },
};

export default widgetModule;
